/**
 * @module features/clients/components/LinkUserModal
 * @description Modal for searching and batch linking unassigned client-role users
 * to an organization, enforcing strict client quota capacity limits.
 */
import * as React from "react";
import { Search, UserPlus, Users, AlertCircle, Copy, Check, Loader2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useAvailableUsers, useLinkUsers } from "../api/clientQueries";
import { cn } from "@/lib/utils";

export interface LinkUserModalProps {
  clientId: string | null;
  clientName: string;
  maxLimit: number;
  currentActiveCount: number;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function LinkUserModal({
  clientId,
  clientName,
  maxLimit,
  currentActiveCount,
  open,
  onClose,
  onSuccess,
}: LinkUserModalProps) {
  const [searchInput, setSearchInput] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([]);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [copiedId, setCopiedId] = React.useState(false);

  const remainingCapacity = Math.max(0, maxLimit - currentActiveCount);

  // Debounce search input by 300ms to prevent flooding the backend with 10-20 API calls per email search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: availableUsers, isLoading, isFetching } = useAvailableUsers(debouncedSearch, open);
  const linkMutation = useLinkUsers();

  const handleCopyId = () => {
    if (!clientId) return;
    try {
      navigator.clipboard.writeText(clientId);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    } catch {
      // Ignore
    }
  };

  React.useEffect(() => {
    if (open) {
      setSelectedUserIds([]);
      setErrorMessage(null);
      setSearchInput("");
      setDebouncedSearch("");
    }
  }, [open]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // Instant query flush on Enter
      setDebouncedSearch(searchInput.trim());
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setDebouncedSearch("");
  };

  const handleToggleUser = (userId: string) => {
    setErrorMessage(null);
    if (selectedUserIds.includes(userId)) {
      setSelectedUserIds(selectedUserIds.filter((id) => id !== userId));
    } else {
      if (selectedUserIds.length >= remainingCapacity) {
        setErrorMessage(
          `Cannot select more users. This organization has only ${remainingCapacity} seat(s) remaining.`
        );
        return;
      }
      setSelectedUserIds([...selectedUserIds, userId]);
    }
  };

  const handleSelectAllAvailable = () => {
    if (!availableUsers) return;
    const canSelect = availableUsers.slice(0, remainingCapacity).map((u) => u.id);
    setSelectedUserIds(canSelect);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId || selectedUserIds.length === 0) return;

    setErrorMessage(null);
    try {
      await linkMutation.mutateAsync({
        clientId,
        userIds: selectedUserIds,
      });
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data?.error_message ||
        err?.message ||
        "Failed to link users. Please try again."
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent sheetOnMobile className="sm:max-w-md max-h-[92dvh] sm:max-h-[85vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="px-4 sm:px-5 pt-3.5 sm:pt-4 pb-3 border-b border-zinc-100 dark:border-zinc-800 pr-10 sm:pr-8 shrink-0">
          <div className="space-y-1">
            <DialogTitle className="text-sm sm:text-base font-bold flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
              <span className="truncate">Link Users</span>
              <span className="hidden sm:inline text-xs text-muted-foreground font-normal truncate max-w-[200px]">
                to {clientName}
              </span>
            </DialogTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-foreground truncate max-w-[220px] sm:hidden">
                {clientName}
              </span>
              {clientId && (
                <button
                  type="button"
                  onClick={handleCopyId}
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-mono text-muted-foreground hover:text-foreground bg-muted/60 hover:bg-muted border border-border/70 transition-colors select-all shadow-2xs shrink-0"
                  title="Click to copy Client Organization ID"
                >
                  <span className="font-semibold text-foreground">ID:</span>
                  <span className="tracking-tight">{clientId}</span>
                  {copiedId ? (
                    <Check className="h-3 w-3 text-emerald-500 shrink-0" />
                  ) : (
                    <Copy className="h-3 w-3 opacity-60 shrink-0" />
                  )}
                </button>
              )}
            </div>
          </div>
          <DialogDescription className="text-xs text-muted-foreground mt-1">
            Assign client users. Capacity:{" "}
            <span className="font-semibold text-foreground">
              {currentActiveCount} / {maxLimit}
            </span>{" "}
            used ({remainingCapacity} available).
          </DialogDescription>
        </DialogHeader>

        {/* Search input with debounce & status indicator */}
        <div className="px-5 pt-3 pb-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground pointer-events-none" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search by name or email (e.g. user@example.com)..."
              className="pl-9 pr-9 h-9 text-xs"
              autoComplete="off"
            />
            {/* Show loading spinner if fetching or debouncing is pending */}
            {isFetching || searchInput.trim() !== debouncedSearch ? (
              <Loader2 className="h-3.5 w-3.5 absolute right-3 top-2.5 text-muted-foreground animate-spin pointer-events-none" />
            ) : searchInput ? (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-2.5 top-2.5 p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
          {debouncedSearch && (
            <div className="flex items-center justify-between text-[11px] text-muted-foreground mt-1.5 px-0.5">
              <span>
                Filtering by: <strong className="text-foreground font-mono">{debouncedSearch}</strong>
              </span>
              <button
                type="button"
                onClick={handleClearSearch}
                className="text-muted-foreground hover:text-foreground hover:underline"
              >
                Reset filter
              </button>
            </div>
          )}
        </div>

        {/* Error alert */}
        {errorMessage && (
          <div className="mx-5 p-2.5 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-xs text-red-600 dark:text-red-400 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Selection summary bar */}
        <div className="px-5 py-1.5 flex items-center justify-between text-xs text-muted-foreground bg-zinc-50/50 dark:bg-zinc-900/30 border-y border-zinc-100 dark:border-zinc-800">
          <span>
            Selected: <strong className="text-foreground">{selectedUserIds.length}</strong> /{" "}
            {remainingCapacity} allowed
          </span>
          {availableUsers && availableUsers.length > 0 && remainingCapacity > 0 && (
            <button
              type="button"
              onClick={handleSelectAllAvailable}
              className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
            >
              Select Max ({Math.min(availableUsers.length, remainingCapacity)})
            </button>
          )}
        </div>

        {/* User List */}
        <div className="flex-1 overflow-y-auto px-5 py-2 divide-y divide-zinc-100 dark:divide-zinc-800/60 min-h-[220px]">
          {isLoading ? (
            <div className="p-8 text-center text-xs text-muted-foreground flex flex-col items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span>Searching unassigned client users...</span>
            </div>
          ) : !availableUsers || availableUsers.length === 0 ? (
            <div className="p-8 text-center text-xs text-muted-foreground space-y-1">
              <Users className="h-7 w-7 mx-auto text-zinc-300 dark:text-zinc-700" />
              <p className="font-medium text-foreground">No unassigned client users found</p>
              <p>
                {debouncedSearch
                  ? `No unassigned client users match "${debouncedSearch}".`
                  : "All client users are either already assigned or none exist."}
              </p>
            </div>
          ) : (
            availableUsers.map((user) => {
              const isSelected = selectedUserIds.includes(user.id);
              const isDisabled =
                !isSelected && selectedUserIds.length >= remainingCapacity;

              return (
                <div
                  key={user.id}
                  onClick={() => !isDisabled && handleToggleUser(user.id)}
                  className={cn(
                    "py-2.5 px-2 rounded-lg flex items-center justify-between gap-3 transition-colors cursor-pointer select-none",
                    isSelected
                      ? "bg-blue-50/60 dark:bg-blue-950/30"
                      : "hover:bg-zinc-50 dark:hover:bg-zinc-800/40",
                    isDisabled && "opacity-40 cursor-not-allowed"
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Checkbox
                      checked={isSelected}
                      disabled={isDisabled}
                      onCheckedChange={() => handleToggleUser(user.id)}
                    />
                    {user.avatar_url ? (
                      <img
                        src={user.avatar_url}
                        alt={user.name}
                        className="h-7 w-7 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <div className="h-7 w-7 rounded-full bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center font-bold text-[10px] shrink-0 text-foreground">
                        {user.name.substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 text-xs">
                      <p className="font-semibold text-foreground truncate">{user.name}</p>
                      <p className="text-muted-foreground text-[11px] truncate">{user.email_id}</p>
                    </div>
                  </div>

                  {isSelected && (
                    <span className="text-[10px] text-blue-600 dark:text-blue-400 font-semibold px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/50 shrink-0">
                      Added
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="p-3.5 sm:p-4 pb-[max(env(safe-area-inset-bottom),1rem)] sm:pb-4 border-t border-zinc-100 dark:border-zinc-800 shrink-0 bg-card/95 dark:bg-zinc-950/95 flex flex-row items-center justify-end gap-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={linkMutation.isPending}
            className="flex-1 sm:flex-initial h-10 sm:h-8 text-xs font-medium"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={selectedUserIds.length === 0 || linkMutation.isPending}
            className="flex-1 sm:flex-initial h-10 sm:h-8 text-xs font-semibold gap-1.5"
          >
            {linkMutation.isPending ? "Linking Users..." : `Link ${selectedUserIds.length} User(s)`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
